import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import {
    animate,
    AnimationBuilder,
    AnimationPlayer,
    style,
} from '@angular/animations';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import {
    delay,
    filter,
    merge,
    ReplaySubject,
    Subject,
    Subscription,
    takeUntil,
} from 'rxjs';
import { fuseAnimations } from '@fuse/animations';
import {
    FuseNavigationItem,
    FuseVerticalNavigationAppearance,
    FuseVerticalNavigationMode,
    FuseVerticalNavigationPosition,
} from '@fuse/components/navigation/navigation.types';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseScrollbarDirective } from '@fuse/directives/scrollbar/scrollbar.directive';
import { FuseUtilsService } from '@fuse/services/utils/utils.service';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ShiftDataService } from '../../../../app/components/shift-data-analysis/shift-data.service';
import { AuthService } from '../../../../app/core/auth/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { DashboardService } from 'app/services/dashboard.service';
import configurationData from 'assets/configuration.json';
@Component({
    selector: 'fuse-vertical-navigation',
    templateUrl: './vertical.component.html',
    styleUrls: ['./vertical.component.scss'],
    animations: fuseAnimations,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'fuseVerticalNavigation',
})
export class FuseVerticalNavigationComponent
    implements OnChanges, OnInit, AfterViewInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_inner: BooleanInput;
    static ngAcceptInputType_opened: BooleanInput;
    static ngAcceptInputType_transparentOverlay: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() appearance: FuseVerticalNavigationAppearance = 'default';
    @Input() autoCollapse: boolean = true;
    @Input() inner: boolean = false;
    @Input() mode: FuseVerticalNavigationMode = 'side';
    @Input() name: string = this._fuseUtilsService.randomId();
    @Input() navigation: FuseNavigationItem[];
    @Input() opened: boolean = true;
    @Input() position: FuseVerticalNavigationPosition = 'left';
    @Input() transparentOverlay: boolean = false;
    @Output()
    readonly appearanceChanged: EventEmitter<FuseVerticalNavigationAppearance> =
        new EventEmitter<FuseVerticalNavigationAppearance>();
    @Output() readonly modeChanged: EventEmitter<FuseVerticalNavigationMode> =
        new EventEmitter<FuseVerticalNavigationMode>();
    @Output() readonly openedChanged: EventEmitter<boolean> =
        new EventEmitter<boolean>();
    @Output()
    readonly positionChanged: EventEmitter<FuseVerticalNavigationPosition> = new EventEmitter<FuseVerticalNavigationPosition>();
    @ViewChild('navigationContent') private _navigationContentEl: ElementRef;

    activeAsideItemId: string | null = null;
    onCollapsableItemCollapsed: ReplaySubject<FuseNavigationItem> =
        new ReplaySubject<FuseNavigationItem>(1);
    onCollapsableItemExpanded: ReplaySubject<FuseNavigationItem> =
        new ReplaySubject<FuseNavigationItem>(1);
    onRefreshed: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    private _animationsEnabled: boolean = false;
    private _asideOverlay: HTMLElement;
    private readonly _handleAsideOverlayClick: any;
    private readonly _handleOverlayClick: any;
    private _hovered: boolean = false;
    private _overlay: HTMLElement;
    private _player: AnimationPlayer;
    private _scrollStrategy: ScrollStrategy =
        this._scrollStrategyOptions.block();
    private _fuseScrollbarDirectives!: QueryList<FuseScrollbarDirective>;
    private _fuseScrollbarDirectivesSubscription: Subscription;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    filtersGroupTitle: FuseNavigationItem = {
        id: 'filters',
        title: this.dashboardService.translate('Filters'),
        type: 'group',
        icon: 'heroicons_outline:home',
    };
    devider: FuseNavigationItem = {
        id: 'divider-1',
        type: 'divider',
    };
    siteName: FuseNavigationItem = {
        id: '5',
        title: this.dashboardService.translate('Site Name'),
        type: 'collapsable',
        icon: 'custom:site-name',
        solid: 'custom_solid:site-name',
        // link: '[]',
        // externalLink: true,
        function: () => {
            this.shiftDataService.openSiteSelectionDialog();
        },
        children: [],
    };
    chainage: FuseNavigationItem = {
        id: '6',
        title: this.dashboardService.translate('Chainage'),
        type: 'collapsable',
        icon: 'custom:chainage',
        solid: 'custom_solid:chainage',
        children: [],
    };
    chainages: FuseNavigationItem = {
        id: '6',
        title: this.dashboardService.translate('Chainages'),
        type: 'basic',
        icon: 'custom:chainage',
        solid: 'custom_solid:chainage',
        function: () => {
            this.shiftDataService.openChainageFilterDialog();
        },
        children: []
    };

    dateFilter: FuseNavigationItem = {
        id: '7',
        title: this.dashboardService.translate('Date Filter'),
        type: 'basic',
        icon: 'custom:date',
        solid: 'custom_solid:date',
        children: [],
        function: () => {
            this.shiftDataService.openDateFilterDialog();
        },
    };
    assets: FuseNavigationItem = {
        id: '8',
        title: this.dashboardService.translate('Inventory'),
        type: 'basic',
        icon: 'custom:site-name',
        solid: 'custom_solid:site-name',
        children: [],
        function: () => {
            this.shiftDataService.openAssetsFilterDialog();
        },
    };

    category: FuseNavigationItem = {
        id: 'category',
        title: this.dashboardService.translate('Category'),
        type: 'basic',
        icon: 'custom:category',
        solid: 'custom_solid:category',
        children: [],
        function: () => {
            this.shiftDataService.openCategoryFilterDialog();
        },
    };

    tickets: FuseNavigationItem = {
        id: 'Tickets',
        title: this.dashboardService.translate('Tickets'),
        type: 'basic',
        icon: 'custom_solid:tickets',
        keyName: 'Tickets',
        children: [],
        function: () => {
            window.open('https://ticket.seekright.com/login.php', '_blank', 'noopener,noreferrer');
        },
    };
    /**upload videos only in jhnsw site */
    upload: FuseNavigationItem = {
        id: 'Upload',
        title: this.dashboardService.translate('Upload Videos'),
        type: 'basic',
        icon: 'material-twotone:upload',
        keyName: 'Upload',
        children: [],
        function: () => {
            window.open(this.uploadFilesUrl, '_blank', 'noopener,noreferrer');
        },
    };

    downloadReport: FuseNavigationItem = {
        id: '9',
        title: this.dashboardService.translate('Download Report'),
        type: 'basic',
        icon: 'custom:download',
        keyName: 'DOWNLOAD_REPORT',
        children: [],
        function: () => {
            this.shiftDataService.openDownloadDialog();
        },
    };

    logout: FuseNavigationItem = {
        id: '10',
        title: this.dashboardService.translate('Log Out'),
        type: 'basic',
        icon: 'custom:logout',
        children: [],
        function: () => {
            this.authService.signOut().subscribe(() => {
                if (window.location.host === 'takeleap.in') {
                    window.location.href = '/seekright-v2-development/#/sign-in'
                }
                else {
                    window.location.href = '/#/sign-in'
                }
            });
        },
    };

    newFeature: FuseNavigationItem = {
        id: '12',
        title: this.dashboardService.translate("What's New"),
        type: 'basic',
        // tooltip : 'New features',
        icon: 'custom:',
        children: [],
        classes: {
            title: 'new-feature-title',
            icon: 'new-feature-icon',
            wrapper: 'new-feature-wrapper'
        },
        function: () => {
            this.showPopup();
        },
    };

    /*verification filter changes*/
    verification_type: FuseNavigationItem = {
        id: '11',
        title: this.dashboardService.translate('Verification'),
        type: 'basic',
        icon: 'custom:site-name',
        solid: 'custom_solid:site-name',
        children: [],
        function: () => {
            this.shiftDataService.openVerificationFilterDialog();
        },
    };
    isWorkOrder: boolean = false;
    /**verification filter changes */
    isVeificationFilterDisabled: boolean = false;
    isDownloadDisable: boolean = false;
    isChainageFilterDisabled: boolean = false;
    displayChainage: boolean = false;
    isShowFilters = true;
    isMaple: boolean = false;
    uploadFiles: boolean = false;/**upload videos only in jhnsw site */
    uploadFilesUrl: string = '';
    loggedInUser: any;
    hideDownloadReport = true;
    isAdmin: boolean = false;
    value: any = '';
    isMasterIDShown: boolean = false;
    isAnomalyIDShown: boolean = false;
    ticketOptionAvailable: boolean = false;
    isMenuOpen = false;
    isAnomalyPage: boolean = false;
    //date filter in dashboard
    //isDashBoard:boolean = false;
    /*Section filter changes */
    /*section_filter: FuseNavigationItem = {
        id: '12',
        title: this.dashboardService.translate('Section'),
        type: 'basic',
        icon: 'custom:site-name',
        solid: 'custom_solid:site-name',
        children: [],
        function: () => {
            this.shiftDataService.openSectionFilterDialog();
        },
    };*/
    /**change password */
    /*changePassword: FuseNavigationItem = {
        id: '13',
        title: this.dashboardService.translate('Change Password'),
        type: 'basic',
        icon: 'custom:site-name',
        solid: 'custom_solid:site-name',
        children: [],
        function: () => {
            this.shiftDataService.openChangePasswordDialog();
        },
    };*/
    /**
     * Constructor
     */
    constructor(
        private _animationBuilder: AnimationBuilder,
        private _changeDetectorRef: ChangeDetectorRef,
        private _elementRef: ElementRef,
        private _renderer2: Renderer2,
        private _router: Router,
        private _scrollStrategyOptions: ScrollStrategyOptions,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseUtilsService: FuseUtilsService,
        private shiftDataService: ShiftDataService,
        private authService: AuthService,
        private dashboardService: DashboardService,
        private dialog: MatDialog
    ) {
        this.loggedInUser = localStorage.getItem('loggedInUser') ? JSON.parse(localStorage.getItem('loggedInUser')) : undefined;
        this._handleAsideOverlayClick = (): void => {
            this.closeAside();
        };
        this._handleOverlayClick = (): void => {
            this.close();
        };
        this.shiftDataService.tabClickEvent.subscribe(() =>
            this.processRouteChange()
        );
        this.isAdmin = localStorage.getItem("isAdmin") === "true" ? true : false;
        let currentPage = localStorage.getItem('currentPage');
        this.isAnomalyPage = (currentPage === "ShiftDataAnalysis") ? true : false;
        this.isAnomalyIDShown = (currentPage === "WorkOrder" || currentPage === "ShiftDataAnalysis") ? true : false;
        this.isMasterIDShown = (currentPage === "MasterDataLibrary") ? true : false;
        this.ticketOptionAvailable = (this.loggedInUser && this.loggedInUser.org_id === 57) ? true : false;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any {
        return {
            'fuse-vertical-navigation-animations-enabled':
                this._animationsEnabled,
            [`fuse-vertical-navigation-appearance-${this.appearance}`]: true,
            'fuse-vertical-navigation-hover': this._hovered,
            'fuse-vertical-navigation-inner': this.inner,
            'fuse-vertical-navigation-mode-over': this.mode === 'over',
            'fuse-vertical-navigation-mode-side': this.mode === 'side',
            'fuse-vertical-navigation-opened': this.opened,
            'fuse-vertical-navigation-position-left': this.position === 'left',
            'fuse-vertical-navigation-position-right':
                this.position === 'right',
        };
    }

    /**
     * Host binding for component inline styles
     */
    @HostBinding('style') get styleList(): any {
        return {
            visibility: this.opened ? 'visible' : 'hidden',
        };
    }

    /**
     * Setter for fuseScrollbarDirectives
     */
    @ViewChildren(FuseScrollbarDirective)
    set fuseScrollbarDirectives(
        fuseScrollbarDirectives: QueryList<FuseScrollbarDirective>
    ) {
        // Store the directives
        this._fuseScrollbarDirectives = fuseScrollbarDirectives;

        // Return if there are no directives
        if (fuseScrollbarDirectives.length === 0) {
            return;
        }

        // Unsubscribe the previous subscriptions
        if (this._fuseScrollbarDirectivesSubscription) {
            this._fuseScrollbarDirectivesSubscription.unsubscribe();
        }

        // Update the scrollbars on collapsable items' collapse/expand
        this._fuseScrollbarDirectivesSubscription = merge(
            this.onCollapsableItemCollapsed,
            this.onCollapsableItemExpanded
        )
            .pipe(takeUntil(this._unsubscribeAll), delay(250))
            .subscribe(() => {
                // Loop through the scrollbars and update them
                fuseScrollbarDirectives.forEach((fuseScrollbarDirective) => {
                    fuseScrollbarDirective.update();
                });
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Decorated methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * On mouseenter
     *
     * @private
     */
    @HostListener('mouseenter')
    private _onMouseenter(): void {
        // Enable the animations
        this._enableAnimations();

        // Set the hovered
        this._hovered = true;
    }

    /**
     * On mouseleave
     *
     * @private
     */
    @HostListener('mouseleave')
    private _onMouseleave(): void {
        // Enable the animations
        this._enableAnimations();

        // Set the hovered
        this._hovered = false;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On changes
     *
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        // Appearance
        if ('appearance' in changes) {
            // Execute the observable
            this.appearanceChanged.next(changes.appearance.currentValue);
        }

        // Inner
        if ('inner' in changes) {
            // Coerce the value to a boolean
            this.inner = coerceBooleanProperty(changes.inner.currentValue);
        }

        // Mode
        if ('mode' in changes) {
            // Get the previous and current values
            const currentMode = changes.mode.currentValue;
            const previousMode = changes.mode.previousValue;

            // Disable the animations
            this._disableAnimations();

            // If the mode changes: 'over -> side'
            if (previousMode === 'over' && currentMode === 'side') {
                // Hide the overlay
                this._hideOverlay();
            }

            // If the mode changes: 'side -> over'
            if (previousMode === 'side' && currentMode === 'over') {
                // Close the aside
                this.closeAside();

                // If the navigation is opened
                if (this.opened) {
                    // Show the overlay
                    this._showOverlay();
                }
            }

            // Execute the observable
            this.modeChanged.next(currentMode);

            // Enable the animations after a delay
            // The delay must be bigger than the current transition-duration
            // to make sure nothing will be animated while the mode changing
            setTimeout(() => {
                this._enableAnimations();
            }, 500);
        }

        // Navigation
        if ('navigation' in changes) {
            // Mark for check
            this._changeDetectorRef.markForCheck();
        }

        // Opened
        if ('opened' in changes) {
            // Coerce the value to a boolean
            this.opened = coerceBooleanProperty(changes.opened.currentValue);

            // Open/close the navigation
            this._toggleOpened(this.opened);
        }

        // Position
        if ('position' in changes) {
            // Execute the observable
            this.positionChanged.next(changes.position.currentValue);
        }

        // Transparent overlay
        if ('transparentOverlay' in changes) {
            // Coerce the value to a boolean
            this.transparentOverlay = coerceBooleanProperty(
                changes.transparentOverlay.currentValue
            );
        }
    }

    /**
     * On init
     */
    ngOnInit(): void {
        // Make sure the name input is not an empty string
        if (this.name === '') {
            this.name = this._fuseUtilsService.randomId();
        }

        // Register the navigation component
        this._fuseNavigationService.registerComponent(this.name, this);
        this.dialog.closeAll();
        this.processRouteChange();
        // Subscribe to the 'NavigationEnd' event
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((r) => {
                let path = (r as NavigationEnd).url;
                this.dialog.closeAll();

                this.processRouteChange();

                // If the mode is 'over' and the navigation is opened...
                if (this.mode === 'over' && this.opened) {
                    // Close the navigation
                    this.close();
                }

                // If the mode is 'side' and the aside is active...
                if (this.mode === 'side' && this.activeAsideItemId) {
                    // Close the aside
                    this.closeAside();
                }
            });
        this.shiftDataService.masterSiteSelectionChange
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.masterSiteSelectionChanges();
            });
    }

    masterSiteSelectionChanges() {
        let masterSite = this.shiftDataService.selectedMasterSite;
        let siteIds: number[] = [];
        if (masterSite != null) {
            for (let i = 0; i < masterSite.sub_sites.length; i++) {
                siteIds[i] = masterSite.sub_sites[i].site_id;
            }
            this.isMaple = configurationData.multipleChainages[siteIds[0].toString()] === undefined ?
                configurationData.multipleChainages['default'] : configurationData.multipleChainages[siteIds[0].toString()];
            //when their is maple new site changes, multiple chainages should be enabled
            if (this.shiftDataService.selectedMasterSite.sub_sites[0].site_id === 178 || this.shiftDataService.selectedMasterSite.sub_sites[0].site_id === 179) {
                this.shiftDataService.chainageValues = [{ index: 0, chainage: [414, 418.989] },
                { index: 1, chainage: [0, 62.1] }
                ]
            }
            else if (this.shiftDataService.selectedMasterSite.sub_sites[0].site_id === 268 || this.shiftDataService.selectedMasterSite.sub_sites[0].site_id === 269) {
                this.shiftDataService.chainageValues = [{ index: 0, chainage: [414, 419.8] },
                { index: 1, chainage: [0, 63.5] }
                ]
            }
            localStorage.setItem("OriginalChainageValues", JSON.stringify(this.shiftDataService.chainageValues));
        }
    }

    processRouteChange() {
        const hash = window.location.hash;
        if (
            hash.includes('/shift-data') ||
            hash.includes('/master-data') ||
            hash.includes('/work-order')
        ) {
            this.isShowFilters = true;
            this.hideDownloadReport = false;
        } else {
            this.isShowFilters = false;
            this.hideDownloadReport = true;
        }
        if (hash.includes('/tracking-module')) {
            this.hideDownloadReport = true;
        }
        this.masterSiteSelectionChanges();
        this.isWorkOrder = hash.includes('/work-order') ? true : false;
        const value = localStorage.getItem('currentWorkOrderTab');
        this.isAnomalyPage = (hash.includes('/shift-data')) ? true : false;
        //verification filter changes
        this.isAnomalyIDShown = (hash.includes('/shift-data') || hash.includes('/work-order')) ? true : false;
        this.isMasterIDShown = (hash.includes('/master-data')) ? true : false;
        this.isVeificationFilterDisabled = value === 'Completed' ? false : true;
        //check if their is anomaly/asset searched, in Anomaly/MDL ,then 'Download Report' should be disabled
        let AnomalyID = localStorage.getItem("AnomalyID");
        let MasterID = localStorage.getItem("MasterID");
        if (hash.includes('/shift-data') || hash.includes('/work-order')) {
            this.isDownloadDisable = (AnomalyID == null || AnomalyID == '') ? false : true;
        }
        if (hash.includes('/master-data')) {
            this.isDownloadDisable = (MasterID == null || MasterID == '') ? false : true;
        }
        //configuration files
        this.displayChainage = configurationData.displayChainage[this.loggedInUser.org_id.toString()] === undefined ?
            configurationData.displayChainage['default'] : configurationData.displayChainage[this.loggedInUser.org_id.toString()];
        /**upload videos only in jhnsw site */
        this.uploadFilesUrl = configurationData.uploadFiles[this.loggedInUser.org_id.toString()] === undefined ?
            configurationData.uploadFiles['default'] : configurationData.uploadFiles[this.loggedInUser.org_id.toString()];
        this.uploadFiles = this.uploadFilesUrl !== '' ? true : false;
        //date filter in dashboard
        //this.isDashBoard = hash.includes('/dashboard') ? true : false ;
    }

    /**
     * After view init
     */
    ngAfterViewInit(): void {
        setTimeout(() => {
            // Return if 'navigation content' element does not exist
            if (!this._navigationContentEl) {
                return;
            }

            // If 'navigation content' element doesn't have
            // perfect scrollbar activated on it...
            if (
                !this._navigationContentEl.nativeElement.classList.contains(
                    'ps'
                )
            ) {
                // Find the active item
                const activeItem =
                    this._navigationContentEl.nativeElement.querySelector(
                        '.fuse-vertical-navigation-item-active'
                    );

                // If the active item exists, scroll it into view
                if (activeItem) {
                    activeItem.scrollIntoView();
                }
            }
            // Otherwise
            else {
                // Go through all the scrollbar directives
                this._fuseScrollbarDirectives.forEach(
                    (fuseScrollbarDirective) => {
                        // Skip if not enabled
                        if (!fuseScrollbarDirective.isEnabled()) {
                            return;
                        }

                        // Scroll to the active element
                        fuseScrollbarDirective.scrollToElement(
                            '.fuse-vertical-navigation-item-active',
                            -120,
                            true
                        );
                    }
                );
            }
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Forcefully close the navigation and aside in case they are opened
        this.close();
        this.closeAside();

        // Deregister the navigation component from the registry
        this._fuseNavigationService.deregisterComponent(this.name);

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Refresh the component to apply the changes
     */
    refresh(): void {
        // Mark for check
        this._changeDetectorRef.markForCheck();

        // Execute the observable
        this.onRefreshed.next(true);
    }

    /**
     * Open the navigation
     */
    open(): void {
        // Return if the navigation is already open
        if (this.opened) {
            return;
        }

        // Set the opened
        this._toggleOpened(true);
    }

    /**
     * Close the navigation
     */
    close(): void {
        // Return if the navigation is already closed
        if (!this.opened) {
            return;
        }

        // Close the aside
        this.closeAside();

        // Set the opened
        this._toggleOpened(false);
    }

    /**
     * Toggle the navigation
     */
    toggle(): void {
        // Toggle
        if (this.opened) {
            this.close();
        } else {
            this.open();
        }
    }

    /**
     * Open the aside
     *
     * @param item
     */
    openAside(item: FuseNavigationItem): void {
        // Return if the item is disabled
        if (item.disabled || !item.id) {
            return;
        }

        // Open
        this.activeAsideItemId = item.id;

        // Show the aside overlay
        this._showAsideOverlay();

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Close the aside
     */
    closeAside(): void {
        // Close
        this.activeAsideItemId = null;

        // Hide the aside overlay
        this._hideAsideOverlay();

        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Toggle the aside
     *
     * @param item
     */
    toggleAside(item: FuseNavigationItem): void {
        // Toggle
        if (this.activeAsideItemId === item.id) {
            this.closeAside();
        } else {
            this.openAside(item);
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Enable the animations
     *
     * @private
     */
    private _enableAnimations(): void {
        // Return if the animations are already enabled
        if (this._animationsEnabled) {
            return;
        }

        // Enable the animations
        this._animationsEnabled = true;
    }

    /**
     * Disable the animations
     *
     * @private
     */
    private _disableAnimations(): void {
        // Return if the animations are already disabled
        if (!this._animationsEnabled) {
            return;
        }

        // Disable the animations
        this._animationsEnabled = false;
    }

    /**
     * Show the overlay
     *
     * @private
     */
    private _showOverlay(): void {
        // Return if there is already an overlay
        if (this._asideOverlay) {
            return;
        }

        // Create the overlay element
        this._overlay = this._renderer2.createElement('div');

        // Add a class to the overlay element
        this._overlay.classList.add('fuse-vertical-navigation-overlay');

        // Add a class depending on the transparentOverlay option
        if (this.transparentOverlay) {
            this._overlay.classList.add(
                'fuse-vertical-navigation-overlay-transparent'
            );
        }

        // Append the overlay to the parent of the navigation
        this._renderer2.appendChild(
            this._elementRef.nativeElement.parentElement,
            this._overlay
        );

        // Enable block scroll strategy
        this._scrollStrategy.enable();

        // Create the enter animation and attach it to the player
        this._player = this._animationBuilder
            .build([
                animate(
                    '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
                    style({ opacity: 1 })
                ),
            ])
            .create(this._overlay);

        // Play the animation
        this._player.play();

        // Add an event listener to the overlay
        this._overlay.addEventListener('click', this._handleOverlayClick);
    }

    /**
     * Hide the overlay
     *
     * @private
     */
    private _hideOverlay(): void {
        if (!this._overlay) {
            return;
        }

        // Create the leave animation and attach it to the player
        this._player = this._animationBuilder
            .build([
                animate(
                    '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
                    style({ opacity: 0 })
                ),
            ])
            .create(this._overlay);

        // Play the animation
        this._player.play();

        // Once the animation is done...
        this._player.onDone(() => {
            // If the overlay still exists...
            if (this._overlay) {
                // Remove the event listener
                this._overlay.removeEventListener(
                    'click',
                    this._handleOverlayClick
                );

                // Remove the overlay
                this._overlay.parentNode.removeChild(this._overlay);
                this._overlay = null;
            }

            // Disable block scroll strategy
            this._scrollStrategy.disable();
        });
    }

    /**
     * Show the aside overlay
     *
     * @private
     */
    private _showAsideOverlay(): void {
        // Return if there is already an overlay
        if (this._asideOverlay) {
            return;
        }

        // Create the aside overlay element
        this._asideOverlay = this._renderer2.createElement('div');

        // Add a class to the aside overlay element
        this._asideOverlay.classList.add(
            'fuse-vertical-navigation-aside-overlay'
        );

        // Append the aside overlay to the parent of the navigation
        this._renderer2.appendChild(
            this._elementRef.nativeElement.parentElement,
            this._asideOverlay
        );

        // Create the enter animation and attach it to the player
        this._player = this._animationBuilder
            .build([
                animate(
                    '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
                    style({ opacity: 1 })
                ),
            ])
            .create(this._asideOverlay);

        // Play the animation
        this._player.play();

        // Add an event listener to the aside overlay
        this._asideOverlay.addEventListener(
            'click',
            this._handleAsideOverlayClick
        );
    }

    /**
     * Hide the aside overlay
     *
     * @private
     */
    private _hideAsideOverlay(): void {
        if (!this._asideOverlay) {
            return;
        }

        // Create the leave animation and attach it to the player
        this._player = this._animationBuilder
            .build([
                animate(
                    '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
                    style({ opacity: 0 })
                ),
            ])
            .create(this._asideOverlay);

        // Play the animation
        this._player.play();

        // Once the animation is done...
        this._player.onDone(() => {
            // If the aside overlay still exists...
            if (this._asideOverlay) {
                // Remove the event listener
                this._asideOverlay.removeEventListener(
                    'click',
                    this._handleAsideOverlayClick
                );

                // Remove the aside overlay
                this._asideOverlay.parentNode.removeChild(this._asideOverlay);
                this._asideOverlay = null;
            }
        });
    }

    /**
     * Open/close the navigation
     *
     * @param open
     * @private
     */
    private _toggleOpened(open: boolean): void {
        // Set the opened
        this.opened = open;

        // Enable the animations
        this._enableAnimations();

        // If the navigation opened, and the mode
        // is 'over', show the overlay
        if (this.mode === 'over') {
            if (this.opened) {
                this._showOverlay();
            } else {
                this._hideOverlay();
            }
        }

        // Execute the observable
        this.openedChanged.next(open);
    }

    //on anomaly value enter ,call enterAnomalyID() and value as paramter which contains entered value
    //it check for current page,if its SDA, then applySearchAnomalyIdFilters() is called
    enterAnomalyID(value: any) {
        value = value.replaceAll(' ', '');
        let currentPage = localStorage.getItem('currentPage');
        if ((currentPage === "ShiftDataAnalysis" || currentPage === "WorkOrder") && value != "") {
            localStorage.setItem("AnomalyID", value);
            this.shiftDataService.anomalyID = value;
            this.shiftDataService.applySearchAnomalyIdFilters(value);
        }
    }

    //on asset value enter ,call enterMasterID() and value as paramter which contains entered value
    //it check for current page,if its MDL, then applySearchMasterIdFilters() is called
    enterMasterID(value: any) {
        value = value.replaceAll(' ', '');
        let currentPage = localStorage.getItem('currentPage');
        if ((currentPage === "MasterDataLibrary") && value != "") {
            this.shiftDataService.masterID = value;
            localStorage.setItem("MasterID", value);
            this.shiftDataService.applySearchMasterIdFilters(value);
        }
    }

    closeMenu(): void {
        this.isMenuOpen = false;
    }

    popupVisible = false;

    showPopup() {
        this.popupVisible = true;
    }

    closePopup() {
        this.popupVisible = false;
    }

}   
